import React, { useState, useEffect } from 'react';
import { Instagram, Smartphone, MapPin, Clock, Star, Zap, Heart, Menu, X, Facebook, MessageCircle, Globe, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/logo_fatou.png'; // Ajuste l'extension si c'est un .svg ou .jpg

// Importation des images des services depuis le dossier assets
import boxBraidsService from '../assets/services/box1.jpeg';
import knotlessBraidsService from '../assets/services/box2.jpeg';
import cornrowsService from '../assets/services/box3.jpeg';

// Importation des images de la galerie depuis le dossier assets
// Box Braids Classic
import galleryBox1 from '../assets/gallery/a1.jpeg';
import galleryBox2 from '../assets/gallery/a2.jpeg';
import galleryBox3 from '../assets/gallery/a3.jpeg';
// Knotless Elegance
import galleryKnotless1 from '../assets/gallery/g1.jpeg';
import galleryKnotless2 from '../assets/gallery/g2.jpeg';
import galleryKnotless3 from '../assets/gallery/g3.jpeg';
// Cornrows Design
import galleryCornrows1 from '../assets/gallery/c1.jpeg';
import galleryCornrows2 from '../assets/gallery/c2.jpeg';
import galleryCornrows3 from '../assets/gallery/c3.jpeg';
// Protective Passion
import galleryProtective1 from '../assets/gallery/p1.jpeg';
import galleryProtective2 from '../assets/gallery/p2.jpeg';
import galleryProtective3 from '../assets/services/box3.jpeg';

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lang, setLang] = useState('es');

  const content = {
    es: {
      nav: { services: "Servicios", gallery: "Galería", book: "Reservar" },
      hero: {
        top: "Expertos en Extensiones",
        subtitle: "Torremolinos, Málaga — Reserva online, paga en el salón",
        cta: "Reserva tu Cita"
      },
      services: {
        title: "Nuestros Servicios",
        collection: "The Collection",
        prepay: "Sin prepago",
        from: "Desde",
        hours: "horas",
        book: "Descubrir & Reservar"
      },
      advantages: {
        exp: { title: "Experiencia", text: "Maestría técnica en extensiones y trenzado, garantizando un acabado natural y duradero." },
        eff: { title: "Eficiencia", text: "Trabajamos con agilidad sin sacrificar el cuidado de tu cabello." },
        well: { title: "Bienestar", text: "Un espacio de relax diseñado para tu comodidad absoluta." }
      },
      testimonials: {
        quote: "Yo tenía extensiones de pelo puestas que me habían puesto en una peluquería muy mal y gracias a Dios🙏encontré esta peluquería africana con esta chica tan tan profesional me dejó la extensiones perfectas estoy muy contenta de haberla conocido este será mi sitio ya seguro lo recomiendo 100%. Super profesional gracias🙏",
        client: "Sandra D. — Cliente Fiel"
      },
      gallery: { title: "Galería de Estilos", subtitle: "Inspiración real para tu próximo cambio de look." },
      footer: {
        desc: "Especialistas en extensiones de alta calidad y trenza tradicional africano en Torremolinos, Málaga. Cuidamos tu estilo y la salud de tu cabello.",
        location: "Ubicación y Horarios",
        follow: "Síguenos",
        closed: "Cerrado"
      }
    },
    en: {
      nav: { services: "Services", gallery: "Gallery", book: "Book Now" },
      hero: {
        top: "Hair Extensions Specialist",
        subtitle: "Torremolinos, Málaga — Book online, pay at the salon",
        cta: "Book your Session"
      },
      services: {
        title: "Our Services",
        collection: "The Collection",
        prepay: "No prepay",
        from: "From",
        hours: "hours",
        book: "Discover & Book"
      },
      advantages: {
        exp: { title: "Expertise", text: "Technical mastery in hair extensions and braiding, ensuring a natural and lasting finish." },
        eff: { title: "Efficiency", text: "We work with agility without sacrificing your hair care." },
        well: { title: "Well-being", text: "A relaxation space designed for your absolute comfort." }
      },
      testimonials: {
        quote: "I had hair extensions put in very poorly at another salon, and thank God🙏 I found this African salon. This girl is so professional, she made my extensions look perfect. I'm very happy to have met her, this will definitely be my place. 100% recommended. Super professional, thank you🙏",
        client: "Sandra D. — Loyal Client"
      },
      gallery: { title: "Style Gallery", subtitle: "Real inspiration for your next look." },
      footer: {
        desc: "Specialists in high-quality extensions and traditional African braiding in Torremolinos, Málaga. We care for your style and hair health.",
        location: "Location & Hours",
        follow: "Follow us",
        closed: "Closed"
      }
    }
  };

  const t = content[lang];

  const reservationLink = "https://www.fresha.com/book-now/fatou-extensiones-mnu4t8du/all-offer?share=true&pId=2856958";

  // Gérer le changement de style au scroll
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      title: "Trenzas",
      price: "Desde 40€",
      duration: "1 hora", 
      image: boxBraidsService,
    },
    {
      title: "Extensiones de nudo",
      price: "Desde 175€",
      duration: "1 - 2 horas",
      image: knotlessBraidsService,
    },
    {
      title: "Extensiones de adhesiva",
      price: "Desde 200€",
      duration: "1 - 3 horas",
      image: cornrowsService,
    },
  ];

  const galleryModels = [
    {
      name: "Bollo can la coleta",
      images: [
        galleryBox1,
        galleryBox2,
        galleryBox3
      ]
    },
    {
      name: "Trenzas Africana",
      images: [
        galleryKnotless1,
        galleryKnotless2,
        galleryKnotless3
      ]
    },
    {
      name: "Extensiones de nudo",
      images: [
        galleryCornrows1,
        galleryCornrows2,
        galleryCornrows3
      ]
    },
    {
      name: "Extensiones de adhesiva",
      images: [
        galleryProtective1,
        galleryProtective2,
        galleryProtective3
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white text-dark font-sans selection:bg-gold selection:text-white overflow-x-hidden">
      {/* Texture de grain subtile pour le look premium */}
      <div className="fixed inset-0 pointer-events-none z-[9999] opacity-[0.03] bg-[url('https://grainy-gradients.vercel.app/noise.svg')]"></div>

      {/* HEADER */}
      <header 
        className={`fixed top-0 w-full z-50 transition-all duration-500 border-b ${
          scrolled 
            ? "bg-white/80 backdrop-blur-xl border-gray-100 py-2 shadow-sm" 
            : "bg-transparent border-transparent py-4"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-8 h-20 md:h-24 flex justify-between items-center relative">
          <motion.div whileHover={{ scale: 1.02 }} className="flex items-center">
            <img 
              src={logo} 
              alt="Fatou Extensions Logo" 
              className={`h-12 md:h-16 w-auto object-contain transition-all duration-500 ${
                !scrolled ? "brightness-0 invert drop-shadow-[0_0_8px_rgba(212,175,55,0.3)]" : "contrast-110"
              }`} 
            />
          </motion.div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-8">
            <a href="#servicios" className={`text-[10px] uppercase tracking-[0.3em] font-bold hover:text-gold transition-colors ${scrolled ? "text-dark/80" : "text-white/90"}`}>{t.nav.services}</a>
            <a href="#galeria" className={`text-[10px] uppercase tracking-[0.3em] font-bold hover:text-gold transition-colors ${scrolled ? "text-dark/80" : "text-white/90"}`}>{t.nav.gallery}</a>
            
            {/* Language Switcher */}
            <button 
              onClick={() => setLang(lang === 'es' ? 'en' : 'es')}
              className={`flex items-center space-x-1 text-[11px] border border-current px-3 py-1.5 rounded-full transition-all hover:bg-gold hover:border-gold hover:text-white font-bold tracking-tighter ${scrolled ? "text-dark" : "text-white"}`}
            >
              <Globe size={12} />
              <span>{lang === 'es' ? 'EN' : 'ES'}</span>
            </button>

            <a 
              href={reservationLink}
              className={`px-8 py-3 rounded-full text-[10px] uppercase tracking-[0.2em] font-bold transition-all duration-500 shadow-xl ${
                scrolled ? "bg-dark text-white hover:bg-gold" : "bg-white text-dark hover:bg-gold hover:text-white"
              }`}
            >
              {t.nav.book}
            </a>
          </div>

          {/* Mobile Toggle */}
          <button aria-label="Menu" className={`md:hidden p-2 ${scrolled ? "text-dark" : "text-white"}`} onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X /> : <Menu />}
          </button>

          <AnimatePresence>
            {isMenuOpen && (
              <motion.div 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="absolute top-20 left-0 w-full bg-white border-b border-gray-100 p-8 flex flex-col space-y-6 md:hidden shadow-2xl z-50"
              >
                <a href="#servicios" onClick={() => setIsMenuOpen(false)} className="text-sm uppercase tracking-[0.3em] font-bold py-2">{t.nav.services}</a>
                <a href="#galeria" onClick={() => setIsMenuOpen(false)} className="text-sm uppercase tracking-[0.3em] font-bold py-2">{t.nav.gallery}</a>
                <button 
                  onClick={() => { setLang(lang === 'es' ? 'en' : 'es'); setIsMenuOpen(false); }}
                  className="text-sm uppercase tracking-[0.3em] font-bold py-2 text-gold text-left flex items-center space-x-2"
                >
                  <Globe size={16} />
                  <span>{lang === 'es' ? 'English Version' : 'Versión Española'}</span>
                </button>
                <a href={reservationLink} className="bg-dark text-white text-center py-4 rounded-full text-xs uppercase tracking-[0.2em] font-bold">
                  {t.hero.cta}
                </a>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>
      </header>

      <main>
        {/* HERO SECTION */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-dark pt-20 md:pt-24">
          <div className="absolute inset-0 z-0">
            <motion.img 
              initial={{ scale: 1.2, opacity: 0 }}
              animate={{ scale: 1.05, opacity: 0.4 }}
              transition={{ duration: 2, ease: "easeOut" }}
              src="https://images.unsplash.com/photo-1595476108010-b4d1f80d9312?q=80&w=1920&auto=format&fit=crop" 
              alt="Modelo con trenzas" 
              className="w-full h-full object-cover animate-pulse-slow"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-dark/40 via-transparent to-dark"></div>
          </div>
          
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.15, delayChildren: 0.4 }
              }
            }}
            className="relative z-10 max-w-7xl mx-auto px-6 w-full text-center"
          >
            <motion.span 
              variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
              className="text-gold text-xs font-bold tracking-[0.6em] uppercase mb-6 block"
            >
              {t.hero.top}
            </motion.span>
            <motion.h1 
              variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } }}
              className="text-5xl sm:text-6xl md:text-9xl font-bold text-white mb-8 leading-[0.9] font-display tracking-tighter uppercase text-center"
            >
              Fatou<br />
              <span className="border-t border-b border-gold/50 py-2 inline-block" style={{ WebkitTextStroke: '1px #D4AF37', WebkitTextFillColor: 'transparent', color: 'transparent' }}>Extensiones</span>
            </motion.h1>
            <motion.p 
              variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
              className="text-gray-400 text-sm md:text-base mb-12 max-w-lg mx-auto uppercase tracking-[0.4em] font-light leading-relaxed"
            >
              {t.hero.subtitle}
            </motion.p>
            <motion.div variants={{ hidden: { opacity: 0, scale: 0.9 }, visible: { opacity: 1, scale: 1 } }}>
              <motion.a 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href={reservationLink}
                className="inline-block bg-white text-dark hover:bg-gold hover:text-white px-12 py-6 rounded-full text-[10px] uppercase tracking-[0.3em] font-black transition-colors shadow-2xl shadow-white/5"
              >
                {t.hero.cta}
              </motion.a>
            </motion.div>
          </motion.div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center">
            <div className="w-[1px] h-12 bg-gradient-to-b from-gold/50 to-transparent"></div>
          </div>
        </section>

        {/* SERVICES */}
        <section id="servicios" className="py-40 max-w-7xl mx-auto px-6">
          <div className="flex flex-col mb-24">
            <div className="flex items-center gap-4 mb-4">
              <span className="text-gold font-bold text-xs tracking-[0.5em] uppercase">{t.services.collection}</span>
              <span className="text-[10px] text-gray-400 uppercase tracking-widest border-l border-gray-200 pl-4">{t.services.prepay}</span>
            </div>
            <div className="max-w-xl">
              <h2 className="text-5xl md:text-7xl font-bold font-display tracking-tighter uppercase leading-none">{t.services.title}</h2>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {services.map((service, index) => (
              <motion.article 
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                key={index} 
                className="group flex flex-col"
              >
                <div className="aspect-[4/5] overflow-hidden mb-10 bg-gray-50 relative">
                  <img 
                    src={service.image} 
                    alt={service.title} 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 ease-out"
                  />
                  <div className="absolute inset-0 bg-dark/0 group-hover:bg-dark/10 transition-colors duration-500"></div>
                </div>
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-2xl font-bold font-display uppercase tracking-tight group-hover:text-gold transition-colors">{service.title}</h3>
                  <span className="text-gold font-bold font-sans">{service.price.replace("Desde", t.services.from)}</span>
                </div>
                <a href={reservationLink} className="text-[9px] uppercase tracking-[0.4em] font-black py-4 border-b border-dark/5 group-hover:border-gold transition-all inline-block w-fit">
                  {t.services.book}
                </a>
              </motion.article>
            ))}
          </div>
        </section>

        {/* ADVANTAGES */}
        <section className="bg-[#1a1a1a] text-white py-24">
          <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-16 text-center">
            <div className="flex flex-col items-center">
              <Star className="text-gold w-6 h-6 mb-8" />
              <h3 className="text-xs uppercase tracking-[0.3em] font-bold mb-4">{t.advantages.exp.title}</h3>
              <p className="text-gray-400 text-sm font-light leading-relaxed">{t.advantages.exp.text}</p>
            </div>
            <div className="flex flex-col items-center">
              <Zap className="text-gold w-6 h-6 mb-8" />
              <h3 className="text-xs uppercase tracking-[0.3em] font-bold mb-4">{t.advantages.eff.title}</h3>
              <p className="text-gray-400 text-sm font-light leading-relaxed">{t.advantages.eff.text}</p>
            </div>
            <div className="flex flex-col items-center">
              <Heart className="text-gold w-6 h-6 mb-8" />
              <h3 className="text-xs uppercase tracking-[0.3em] font-bold mb-4">{t.advantages.well.title}</h3>
              <p className="text-gray-400 text-sm font-light leading-relaxed">{t.advantages.well.text}</p>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-32 bg-white overflow-hidden">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col items-center text-center">
              <span className="text-gold text-6xl font-serif mb-8 italic">"</span>
              <h2 className="text-3xl md:text-5xl font-display italic text-dark mb-12 max-w-4xl leading-tight">
                "{t.testimonials.quote}"
              </h2>
              <div className="flex items-center space-x-4">
                <div className="w-12 h-[1px] bg-gold"></div>
                <cite className="text-xs uppercase tracking-[0.3em] font-bold not-italic">{t.testimonials.client}</cite>
                <div className="w-12 h-[1px] bg-gold"></div>
              </div>
            </div>
          </div>
        </section>

        {/* SOCIAL PROOF */}
        <section id="galeria" className="py-32 max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 space-y-4 md:space-y-0">
            <div>
              <h2 className="text-4xl font-bold uppercase font-display tracking-tight">{t.gallery.title}</h2>
              <p className="text-gray-400 mt-4 font-light italic">{t.gallery.subtitle}</p>
            </div>
            <a href="https://www.instagram.com/fatou.extensiones/" className="flex items-center text-xs uppercase tracking-widest font-bold border-b border-gold pb-2 hover:text-gold transition-colors">
              @fatou.extensiones <Instagram className="ml-2 w-5 h-5" />
            </a>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {galleryModels.map((model, i) => (
              <motion.div 
                key={i} 
                className="aspect-[3/4] bg-gray-100 overflow-hidden cursor-pointer relative group"
                onClick={() => {
                  setSelectedModel(model);
                  setCurrentImageIndex(0);
                }}
                whileHover={{ scale: 0.98 }}
              >
                <img 
                  src={model.images[0]} 
                  alt={model.name} 
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center p-4">
                  <p className="text-white text-center font-bold uppercase tracking-widest text-xs translate-y-4 group-hover:translate-y-0 transition-transform duration-300">{model.name}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="bg-dark text-white pt-20 pb-10 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
          <div className="flex flex-col items-start">
            {/* On utilise filter brightness/invert pour rendre le logo blanc sur le fond noir du footer */}
            <img src={logo} alt="Fatou Extensions" className="h-16 w-auto object-contain mb-6 brightness-0 invert" />
            <p className="text-gray-400 leading-relaxed">
              {t.footer.desc}
            </p>
          </div>
          
          <div>
            <h4 className="text-gold font-bold uppercase mb-6 tracking-widest text-sm">{t.footer.location}</h4>
            <ul className="space-y-4 text-gray-400">
              <li className="flex items-start"><MapPin className="w-5 h-5 mr-3 text-gold shrink-0" /> C. Doña María Barrabino, 2, 29620 Torremolinos, Málaga</li>
              <li className="flex items-start">
                <Clock className="w-5 h-5 mr-3 text-gold shrink-0" />
                <div className="flex flex-col text-xs space-y-1">
                  <span className="font-bold text-gray-300 uppercase tracking-tighter">Mar - Vie: 10:00–14:00, 17:30–20:00</span>
                  <span>Sáb: 11:00–15:00</span>
                  <span className="text-gray-500 italic">Dom - Lun: {t.footer.closed}</span>
                </div>
              </li>
              <li className="flex items-start"><Smartphone className="w-5 h-5 mr-3 text-gold shrink-0" /> +34 687 34 08 65</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-gold font-bold uppercase mb-6 tracking-widest text-sm">{t.footer.follow}</h4>
            <div className="flex space-x-4 items-center">
              {[
                { icon: <Instagram size={20} />, href: "https://www.instagram.com/fatou.extensiones/" },
                { icon: <Facebook size={20} />, href: "https://www.facebook.com/profile.php?id=100058873189969" },
                { 
                  icon: (
                    <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1z"/>
                    </svg>
                  ), 
                  href: "https://tiktok.com/"
                }
              ].map((social, i) => (
                <motion.a
                  key={i}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ y: -5, scale: 1.1 }}
                  className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-white/80 hover:text-gold hover:border-gold transition-all duration-300 bg-white/5 shadow-lg backdrop-blur-sm"
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 pt-10 border-t border-white/5 text-center text-gray-500 text-sm">
          <p>© {new Date().getFullYear()} Fatou Extensions. Todos los derechos reservados.</p>
        </div>
      </footer>

      {/* Bouton WhatsApp Flottant */}
      <motion.a
        href="https://wa.me/34687340865"
        target="_blank"
        rel="noopener noreferrer"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 2, type: "spring", stiffness: 260, damping: 20 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="fixed bottom-6 right-6 md:bottom-10 md:right-10 z-[100] bg-[#25D366] text-white p-4 rounded-full shadow-2xl hover:shadow-[#25D366]/40 transition-shadow flex items-center justify-center"
        aria-label="Contactar por WhatsApp"
      >
        <MessageCircle className="w-7 h-7 fill-current" />
      </motion.a>

      {/* Lightbox / Slider interactif */}
      <AnimatePresence>
        {selectedModel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-dark/98 flex items-center justify-center p-4"
          >
            <button 
              onClick={() => setSelectedModel(null)}
              className="absolute top-8 right-8 text-white/70 hover:text-white z-[120] transition-colors"
            >
              <X size={32} />
            </button>

            <div className="relative w-full max-w-5xl h-full flex items-center justify-center">
              <button 
                className="absolute left-0 md:-left-16 z-[120] text-white/50 hover:text-white transition-colors p-2"
                onClick={() => setCurrentImageIndex((prev) => (prev === 0 ? selectedModel.images.length - 1 : prev - 1))}
              >
                <ChevronLeft size={48} />
              </button>

              <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentImageIndex}
                    src={selectedModel.images[currentImageIndex]}
                    initial={{ opacity: 0, scale: 0.95, x: 20 }}
                    animate={{ opacity: 1, scale: 1, x: 0 }}
                    exit={{ opacity: 0, scale: 0.95, x: -20 }}
                    transition={{ duration: 0.4, ease: "circOut" }}
                    alt={selectedModel.name}
                    className="max-w-full max-h-[80vh] object-contain shadow-2xl rounded-lg"
                  />
                </AnimatePresence>
                <div className="absolute bottom-4 left-0 w-full text-center">
                  <p className="text-white/60 text-[10px] uppercase tracking-[0.3em] font-bold">
                    {selectedModel.name} — {currentImageIndex + 1} / {selectedModel.images.length}
                  </p>
                </div>
              </div>

              <button 
                className="absolute right-0 md:-right-16 z-[120] text-white/50 hover:text-white transition-colors p-2"
                onClick={() => setCurrentImageIndex((prev) => (prev === selectedModel.images.length - 1 ? 0 : prev + 1))}
              >
                <ChevronRight size={48} />
              </button>

              <div className="absolute -bottom-8 flex space-x-3">
                {selectedModel.images.map((_, idx) => (
                  <div key={idx} className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${currentImageIndex === idx ? "bg-gold w-8" : "bg-white/20"}`} />
                ))}
              </div>
            </div>
            <div className="absolute inset-0 z-[115]" onClick={() => setSelectedModel(null)}></div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingPage;
